import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, CheckCircle, TrendingUp, DollarSign, BarChart3, Bot, Loader2 } from 'lucide-react';

const CifrasValidation = ({ uploadedFiles = [] }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(true);
  const [showResults, setShowResults] = useState(false);

  const accountingData = {
    activoTotal: 7202588,
    pasivoPatrimonio: 7202588,
    excedenteEjercicio: 996769,
    coincidenciaEstados: true
  };

  const processingSteps = [
    {
      id: 1,
      title: 'Obtener valores reportados',
      description: 'Extrayendo valores del estado financiero y comparando con notas',
      duration: 2000
    },
    {
      id: 2,
      title: 'Detectar subcategorías',
      description: 'Escaneando Nota 4 para identificar componentes',
      duration: 2500
    },
    {
      id: 3,
      title: 'Calcular efectivo restringido',
      description: 'Sumando todas las subcuentas restringidas',
      duration: 1500
    },
    {
      id: 4,
      title: 'Ajustar clasificación',
      description: 'Reclasificando efectivo restringido',
      duration: 2000
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('COP', '$');
  };

  const validationChecks = [
    {
      title: 'Ecuación Contable Básica',
      description: 'Activo = Pasivo + Patrimonio',
      status: 'verified',
      icon: Calculator
    },
    {
      title: 'Consistencia de Estados',
      description: 'Excedente coincide entre estados',
      status: 'verified', 
      icon: BarChart3
    },
    {
      title: 'Integridad de Cifras',
      description: 'Todas las cifras son consistentes',
      status: 'verified',
      icon: TrendingUp
    }
  ];

  useEffect(() => {
    if (uploadedFiles.length > 0 && isProcessing) {
      const processSteps = async () => {
        for (let i = 0; i < processingSteps.length; i++) {
          setCurrentStep(i);
          await new Promise(resolve => setTimeout(resolve, processingSteps[i].duration));
        }
        setIsProcessing(false);
        setTimeout(() => setShowResults(true), 500);
      };
      
      processSteps();
    }
  }, [uploadedFiles, isProcessing]);

  if (isProcessing) {
    return (
      <div style={{ padding: '24px', width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white', padding: '12px', borderRadius: '12px' }}>
            <Bot size={24} />
          </div>
          <div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
              IA Procesando Información Financiera
            </h2>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '16px' }}>
              Analizando documentos y aplicando reglas contables automáticamente
            </p>
          </div>
        </div>

        {/* Processing Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {processingSteps.map((step, index) => {
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            const isPending = index > currentStep;

            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0.5, scale: 0.98 }}
                animate={{ 
                  opacity: isActive ? 1 : (isCompleted ? 0.8 : 0.5),
                  scale: isActive ? 1 : 0.98,
                  borderColor: isActive ? '#3b82f6' : (isCompleted ? '#10b981' : '#e5e7eb')
                }}
                style={{
                  background: isActive ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' : 
                             isCompleted ? 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)' : 'white',
                  border: `2px solid ${isActive ? '#3b82f6' : (isCompleted ? '#10b981' : '#e5e7eb')}`,
                  borderRadius: '16px',
                  padding: '24px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Background pattern */}
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '100px',
                  height: '100px',
                  background: isActive ? 'rgba(59, 130, 246, 0.1)' : 
                             isCompleted ? 'rgba(16, 185, 129, 0.1)' : 'rgba(229, 231, 235, 0.1)',
                  borderRadius: '50%'
                }} />

                <div style={{ position: 'relative', zIndex: 1 }}>
                  {/* Step Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <div style={{
                      background: isActive ? '#3b82f6' : (isCompleted ? '#10b981' : '#9ca3af'),
                      color: 'white',
                      padding: '12px',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '48px',
                      height: '48px'
                    }}>
                      {isActive ? (
                        <Loader2 size={24} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                      ) : isCompleted ? (
                        <CheckCircle size={24} />
                      ) : (
                        <span style={{ fontSize: '18px', fontWeight: '700' }}>{step.id}</span>
                      )}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <h3 style={{ 
                        margin: '0 0 4px 0', 
                        fontSize: '18px', 
                        fontWeight: '700',
                        color: isActive ? '#1e40af' : (isCompleted ? '#065f46' : '#6b7280')
                      }}>
                        Paso {step.id} – {step.title}
                      </h3>
                      <p style={{ 
                        margin: 0, 
                        fontSize: '14px',
                        color: isActive ? '#3b82f6' : (isCompleted ? '#059669' : '#9ca3af')
                      }}>
                        {step.description}
                      </p>
                    </div>

                    {/* Status indicator */}
                    <div style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: isActive ? '#dbeafe' : (isCompleted ? '#d1fae5' : '#f3f4f6'),
                      color: isActive ? '#1e40af' : (isCompleted ? '#065f46' : '#6b7280'),
                      border: `1px solid ${isActive ? '#3b82f6' : (isCompleted ? '#10b981' : '#d1d5db')}`
                    }}>
                      {isActive ? 'PROCESANDO...' : (isCompleted ? 'COMPLETADO' : 'PENDIENTE')}
                    </div>
                  </div>

                  {/* Step Details */}
                  {(isActive || isCompleted) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      style={{
                        background: isActive ? 'rgba(59, 130, 246, 0.05)' : 'rgba(16, 185, 129, 0.05)',
                        border: `1px solid ${isActive ? 'rgba(59, 130, 246, 0.2)' : 'rgba(16, 185, 129, 0.2)'}`,
                        borderRadius: '8px',
                        padding: '12px'
                      }}
                    >
                      <div style={{
                        fontSize: '13px',
                        color: isActive ? '#1e40af' : '#065f46',
                        fontFamily: 'monospace'
                      }}>
                        {isActive ? '→ Procesando...' : '→ Completado ✓'}
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
              Progreso del Análisis IA
            </span>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              {Math.round(((currentStep + 1) / processingSteps.length) * 100)}%
            </span>
          </div>
          <div style={{ 
            width: '100%', 
            height: '8px', 
            background: '#f3f4f6', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / processingSteps.length) * 100}%` }}
              transition={{ duration: 0.5 }}
              style={{
                height: '100%',
                background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)',
                borderRadius: '4px'
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white', padding: '12px', borderRadius: '12px' }}>
          <Calculator size={24} />
        </div>
        <div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
            Validación contable automática
          </h2>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '16px' }}>
            Verificación de reglas contables básicas y consistencia de cifras
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <AnimatePresence>
        {showResults && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '32px', alignItems: 'start' }}
          >
            
            {/* Left Side - Validation Checks */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {validationChecks.map((check, index) => {
                const Icon = check.icon;
                
                return (
                  <motion.div
                    key={check.title}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.2 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '20px',
                      background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                      border: '2px solid #10b981',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)'
                    }}
                  >
                    <div style={{
                      background: '#10b981',
                      color: 'white',
                      padding: '12px',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon size={24} />
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600', color: '#065f46' }}>
                        {check.title}
                      </h3>
                      <p style={{ margin: 0, fontSize: '14px', color: '#047857' }}>
                        {check.description}
                      </p>
                    </div>
                    
                    <div style={{
                      background: '#10b981',
                      color: 'white',
                      padding: '8px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CheckCircle size={20} />
                    </div>
                  </motion.div>
                );
              })}

              {/* Agent Confirmation Message */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                style={{
                  background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                  border: '2px solid #3b82f6',
                  borderRadius: '16px',
                  padding: '24px',
                  marginTop: '16px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Background decoration */}
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '80px',
                  height: '80px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '50%'
                }} />

                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{
                      background: '#3b82f6',
                      color: 'white',
                      padding: '8px',
                      borderRadius: '50%'
                    }}>
                      <Calculator size={20} />
                    </div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1e40af' }}>
                      Confirmación del Agente IA
                    </h3>
                  </div>
                  
                  <p style={{ 
                    margin: 0, 
                    fontSize: '16px', 
                    color: '#1e40af',
                    fontWeight: '500',
                    lineHeight: '1.5'
                  }}>
                    "El agente confirma que la ecuación contable se cumple. Todos los valores están balanceados correctamente y no se detectaron inconsistencias en los estados financieros."
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Side - Panel Lateral with Financial Data */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              style={{
                background: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                position: 'sticky',
                top: '20px'
              }}
            >
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  color: '#1f2937',
                  textAlign: 'center',
                  paddingBottom: '16px',
                  borderBottom: '2px solid #f3f4f6'
                }}>
                  Panel Contable
                </h3>
              </div>

              {/* Financial Metrics */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                {/* Activo Total */}
                <div style={{
                  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                  border: '1px solid #3b82f6',
                  borderRadius: '12px',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <TrendingUp size={16} style={{ color: '#1d4ed8' }} />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
                      Activo Total
                    </span>
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e40af' }}>
                    {formatCurrency(accountingData.activoTotal)}
                  </div>
                </div>

                {/* Pasivo + Patrimonio */}
                <div style={{
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  border: '1px solid #f59e0b',
                  borderRadius: '12px',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <BarChart3 size={16} style={{ color: '#d97706' }} />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                      Pasivo + Patrimonio
                    </span>
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#92400e' }}>
                    {formatCurrency(accountingData.pasivoPatrimonio)}
                  </div>
                </div>

                {/* Balance Check */}
                <div style={{
                  background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                  border: '2px solid #10b981',
                  borderRadius: '12px',
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <CheckCircle size={32} style={{ color: '#10b981', marginBottom: '8px' }} />
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>
                    ECUACIÓN BALANCEADA
                  </div>
                  <div style={{ fontSize: '12px', color: '#047857' }}>
                    Activo = Pasivo + Patrimonio ✓
                  </div>
                </div>

                {/* Separator */}
                <div style={{ height: '1px', background: '#e5e7eb', margin: '8px 0' }} />

                {/* Excedente del Ejercicio */}
                <div style={{
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                  border: '1px solid #22c55e',
                  borderRadius: '12px',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <DollarSign size={16} style={{ color: '#16a34a' }} />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#15803d' }}>
                      Excedente del Ejercicio
                    </span>
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#15803d', marginBottom: '8px' }}>
                    {formatCurrency(accountingData.excedenteEjercicio)}
                  </div>
                  
                  {/* Coincidencia Badge */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#dcfce7',
                    padding: '6px 10px',
                    borderRadius: '20px',
                    border: '1px solid #22c55e'
                  }}>
                    <CheckCircle size={14} style={{ color: '#16a34a' }} />
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#15803d' }}>
                      Coincidencia con Estado de Resultados
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completed Steps Summary */}
      <AnimatePresence>
        {showResults && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              border: '2px solid #e2e8f0',
              borderRadius: '16px',
              padding: '24px',
              marginTop: '32px'
            }}
          >
            <h3 style={{ 
              margin: '0 0 20px 0', 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#1f2937',
              textAlign: 'center'
            }}>
              Pasos Completados del Análisis IA
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {processingSteps.map((step, index) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.4 + index * 0.1 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    border: '1px solid #10b981',
                    borderRadius: '12px'
                  }}
                >
                  <div style={{
                    background: '#10b981',
                    color: 'white',
                    padding: '8px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '32px',
                    height: '32px'
                  }}>
                    <CheckCircle size={16} />
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <h4 style={{ 
                      margin: '0 0 2px 0', 
                      fontSize: '14px', 
                      fontWeight: '600',
                      color: '#065f46'
                    }}>
                      Paso {step.id} – {step.title}
                    </h4>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '12px',
                      color: '#047857'
                    }}>
                      {step.description}
                    </p>
                  </div>

                  <div style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '10px',
                    fontWeight: '600',
                    background: '#d1fae5',
                    color: '#065f46',
                    border: '1px solid #10b981'
                  }}>
                    ✓ COMPLETADO
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Progress indicator */}
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                border: '2px solid #3b82f6',
                borderRadius: '20px',
                padding: '8px 16px'
              }}>
                <div style={{
                  width: '20px',
                  height: '8px',
                  background: 'linear-gradient(90deg, #10b981, #3b82f6)',
                  borderRadius: '4px'
                }} />
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af' }}>
                  Pasos 1-4 completados • Continuando con Nota 5...
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CifrasValidation; 